import { DeviceStatus, type Home, UserRole } from '@lmc/ts-common'
import {
  Button,
  ConfirmationModal,
  EmptyState,
  PageHeader,
  PermissionIcon
} from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import { useRoleAccess } from 'components/AccessControl'
import useDevicesAccess from 'components/AccessControl/features/useDevicesAccess'
import useGroupAccess from 'components/AccessControl/useGroupAccess'
import { NAMESPACE } from 'locale'
import { dacConstants } from 'locale/deviceAccessControl'
import { generalConstants } from 'locale/general'
import { type FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from 'react-query'
import { useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import styled from 'styled-components'
import { DevicesManageTable } from './components/DevicesManageTable'
import { InlineNameEdit } from './components/InlineNameEdit'
import { useDeviceById, useUpdateDeviceById } from './hooks'

const HELP_ENABLED =
  'https://support.logmycare.co.uk/en/articles/8010671-how-do-i-manage-devices-in-my-care-home'

const HELP_DISABLED =
  'https://support.logmycare.co.uk/en/articles/8010663-how-do-i-set-up-device-access-control'

export const DeviceManageScreen: FC = () => {
  const navigate = useNavigate()

  const params = useParams()

  const { id } = params

  const queryClient = useQueryClient()

  const hasGroupAccess = useGroupAccess()

  const hasDeviceAccessControl = useDevicesAccess()

  const hasRolePermission = useRoleAccess(UserRole.careHomeAdmin)

  const [selectedHomes, setSelectedHomes] = useState<string[]>([])

  const [multiApprove, setMultiApprove] = useState(false)

  const [isInlineEditing, setIsInlinEditing] = useState<boolean | undefined>(
    false
  )

  const { device, isLoading } = useDeviceById(id, hasRolePermission)

  const { updateDevice, isLoading: isLoadingUpdateDevice } =
    useUpdateDeviceById(() => {
      queryClient.invalidateQueries({ queryKey: [`device-${id}`] })
      setSelectedHomes([])
      setMultiApprove(false)
    })

  const {
    updateDevice: updateDeviceName,
    isLoading: isLoadingUpdateDeviceName,
    error: errorName,
    reset: resetName
  } = useUpdateDeviceById(() => {
    queryClient.invalidateQueries({ queryKey: [`device-${id}`] })
  })

  const { t } = useTranslation(NAMESPACE.DEVICE_ACCESS_CONTROL)

  const homeId = useSelector(
    (state: { global: { home: Home } }) => state.global.home
  )?.id

  if (!hasRolePermission) {
    return (
      <Wrapper>
        <PageHeader
          title={t(dacConstants.DEVICES)}
          moreInfoLink={HELP_DISABLED}
        />
        <EmptyState
          title={t(dacConstants.PSST)}
          description={t(dacConstants.NO_PERMISSIONS)}
          icon={<PermissionIcon />}
        />
      </Wrapper>
    )
  }

  const handleMultiApprove = () => {
    setMultiApprove(true)
    const homes = selectedHomes.map((id) => ({
      home: id,
      status: DeviceStatus.approved
    }))

    updateDevice({ id, homes })
  }

  const hasHomesSelected = !!selectedHomes.length

  return (
    <Wrapper data-testid="device-manage-screen">
      <PageHeader
        title={t(dacConstants.MANAGE_DEVICE)}
        onBack={() => navigate(-1)}
        moreInfoLink={HELP_ENABLED}
        testId="device-manage-header"
      >
        <Button
          disabled={isInlineEditing}
          variant="text"
          onClick={() =>
            navigate(`/organisation/devices/signs/?deviceId=${device.id}`)
          }
          data-cy="btn-view-all-signs"
          data-testid="device-manage-view-signs-button"
        >
          {t(dacConstants.VIEW_ALL)}
        </Button>
      </PageHeader>
      <FormWrapper data-testid="device-manage-form">
        <InlineNameEdit
          isLoading={isLoadingUpdateDeviceName}
          value={device?.name}
          label={t(dacConstants.DEVICE_NAME)}
          isInlineEditing={isInlineEditing}
          handleUpdateDevice={(name) => {
            updateDeviceName({ name, id })
          }}
          setIsInlinEditing={setIsInlinEditing}
          error={errorName as string}
          onReset={resetName}
          testId="device-manage-name-edit"
        />
        <InlineNameEdit
          value={device?.model || ''}
          label={t(dacConstants.MODEL_NAME)}
          disabled
          testId="device-manage-model-edit"
        />
        <InlineNameEdit
          value={device?.version || 'N/A'}
          label={t(dacConstants.APP_VERSION)}
          disabled
          testId="device-manage-version-edit"
        />
      </FormWrapper>
      {hasDeviceAccessControl && hasGroupAccess && (
        <ActionsWrapper>
          <ConfirmationModal
            localisation={{
              confirmText: t(generalConstants.CONFIRM),
              title: t(dacConstants.APPROVE_DEVICE_ALL_SELECTED),
              cancelText: t(generalConstants.CANCEL)
            }}
            modalContent={
              <ModalContentWrapper>
                <Text level={1}>
                  {t(dacConstants.APPROVE_DEVICE_ALL_SELECTED_DESC)}
                </Text>
                <StyledTextDesc level={1}>
                  {t(dacConstants.ARE_YOU_SURE_YOU_WANT_APPROVE_THIS_SITE_ALL)}
                </StyledTextDesc>
              </ModalContentWrapper>
            }
            confirmButtonProps={{ variant: 'primary' }}
          >
            <Button
              disabled={!hasHomesSelected || isInlineEditing}
              onClick={handleMultiApprove}
              isLoading={multiApprove && isLoadingUpdateDevice}
              data-cy="btn-approve-selected"
            >
              {t(dacConstants.APPROVE_SELECTED)}
            </Button>
          </ConfirmationModal>
          <StyledText level={2}>{`${
            hasHomesSelected ? selectedHomes.length : ''
          } ${t(
            hasHomesSelected
              ? dacConstants.SITES_SELECTED
              : dacConstants.SELECT_ITEMS
          )}`}</StyledText>
        </ActionsWrapper>
      )}

      {hasDeviceAccessControl && (
        <TableWrapper data-testid="device-manage-table-wrapper">
          <DevicesManageTable
            device={device}
            updateDevice={updateDevice}
            id={id}
            isLoading={isLoading}
            isLoadingUpdateDevice={isLoadingUpdateDevice}
            isInlineEditing={isInlineEditing}
            selectedHomes={selectedHomes}
            setSelectedHomes={setSelectedHomes}
            hasGroupAccess={hasGroupAccess}
            homeId={homeId}
            testId="device-manage-table"
          />
        </TableWrapper>
      )}
    </Wrapper>
  )
}

const Wrapper = styled.div`
  margin: 0 auto;
  padding: 24px;
  width: 100%;
  max-width: 1080px;
`

const TableWrapper = styled.div`
  overflow-y: auto;
  padding-top: 24px;
  padding-bottom: 24px;
`

const ActionsWrapper = styled.div`
  display: flex;
  align-items: center;
  padding-top: 24px;
`

const StyledText = styled(Text)`
  color: ${(props) => props.theme.color.neutral.color4};
  margin-left: 16px;
`

const StyledTextDesc = styled(Text)`
  font-weight: 500;
`

const FormWrapper = styled.div`
  width: 100%;
  margin-top: 24px;
  padding: 14px 24px;
  background-color: ${(props) => props.theme.color.neutral.color11};
  border-radius: 20px;
  display: flex;
  flex-direction: column;
`

const ModalContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
`

export default DeviceManageScreen
